/**
 * useDesignFrame — does this viewport get the imported assistant design?
 *
 * NOT `useIsDesktop`. That hook breaks at 768px and drives which *behaviours*
 * are desktop-shaped (a sheet vs. a panel). The
 * design is authored at `min-width: 1200px`: a permanent 256px rail beside a
 * 780px column, with a four-column estimate table inside it. At 1024px those
 * columns collide. So the two thresholds are separate on purpose — 768–1199px
 * keeps today's composition, which already works there. Widening this to 768
 * would ship a broken table to every tablet (spec §6).
 *
 * DUPLICATED THRESHOLD, DELIBERATELY. `AppShell` carries the same 1200px query
 * as `DESIGN_HEADER_QUERY` for the design header. Neither imports the other:
 * `app/` reaching into `features/chat/` inverts the dependency, and this repo
 * already settles that trade the same way — `features/chat/useIsDesktop.ts` and
 * `features/shops/useIsDesktop.ts` are the same eight lines twice, each local
 * to its feature. If one threshold moves, the other must move by hand; the
 * design ships them as one frame, so they should never diverge.
 */
import { useEffect, useState } from "react";

import { useDesignWorld } from "@/app/shell/useDesignWorld";

/** The imported design's own frame: below it, the design draws no layout. */
export const DESIGN_FRAME_QUERY = "(min-width: 1200px)";

function readMatch(): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false;
  }
  return window.matchMedia(DESIGN_FRAME_QUERY).matches;
}

export function useDesignFrame(): boolean {
  const [wide, setWide] = useState<boolean>(readMatch);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }
    const mql = window.matchMedia(DESIGN_FRAME_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
      setWide(event.matches);
    };
    // Sync once in case the value changed between initial read and effect.
    setWide(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return wide;
}

/**
 * Does this render get the imported assistant design at all?
 *
 * The full gate, in one place, because three surfaces ask it — the frame, the
 * rail inside the frame, and `ChatRoute` (which drops the product's own motif
 * when the design supplies its ground). Answered differently in any one of
 * them, the surface would render half-designed.
 *
 * The second half is theme, not width. The imported world ships ONE palette
 * and no dark ramp (Ruling R17): the frame paints the cream `--gd-ground`
 * while the transcript inside it still carries `--g-*` ink, which resolves
 * nearly white in dark theme. So dark theme keeps today's composition — the
 * same call the desktop header makes, for the same reason.
 *
 * IMPORT NOTE: `useDesignWorld` sits under `app/shell/` because the header
 * needed it first. It is a design-world concern rather than a shell one;
 * duplicating its theme resolution here would give two surfaces two ways to
 * answer one question. If a third feature needs it, move it to `lib/`.
 */
export function useAssistantDesign(): boolean {
  const wide = useDesignFrame();
  const light = useDesignWorld();
  return wide && light;
}

export default useDesignFrame;
